import { type FC, useRef, useLayoutEffect, useState } from 'react';
import cx from 'classnames';

import styles from './DesktopSideMenu.module.css';

export type DesktopMenuItem = {
  /** Stable React key — `'welcome'` or the banner image id. */
  key: string;
  /** Visible label (already localised by the caller). */
  label: string;
};

type Props = {
  items: DesktopMenuItem[];
  activeIndex: number;
  onSelect: (index: number) => void;
  /** Disabled while a slide transition is in flight — prevents clicks
   *  from queueing up partial animations. */
  busy?: boolean;
};

/** Width + gap of the indicator bar — kept in sync with `.indicator`
 *  width in the module CSS. */
const INDICATOR_WIDTH_PX = 80;
const INDICATOR_GAP_PX = 6;

/**
 * Vertical side menu for the desktop intro overlay. Renders one
 * `<button>` per slide. A single 80×4 px indicator bar is rendered
 * OUTSIDE the items (sibling of all buttons inside the menu container)
 * and absolutely positioned via JS measurements of the active item; CSS
 * transitions on `top` + `left` smooth the bar's slide between active
 * items.
 *
 * Implementation notes:
 * - Earlier impl used `react-flip-toolkit` with `Flipped` mounted /
 *   unmounted inside each item button. That worked in LTR but the
 *   indicator visually glitched in RTL (the toolkit's snapshot deltas
 *   appear to disagree with the browser's RTL flex layout). A
 *   measured absolute position with CSS transitions is direction-
 *   agnostic and reliable.
 * - Items keep their natural content width (`label` only). Indicator
 *   sits in the gutter to the inline-start side of the label (LEFT in
 *   LTR, RIGHT in RTL), 6px gap. Menu's `overflow: visible` lets the
 *   indicator extend beyond the menu container's box if the active
 *   label is wider than the menu width.
 *
 * Inactive labels are 50% opaque white; active is full opacity. Text
 * shadow stack matches design (`#26262680` triple stop) so labels read
 * cleanly over any 3D backdrop. Click on any item fires `onSelect`
 * (which the parent uses to drive camera fly-to + slide change).
 */
export const DesktopSideMenu: FC<Props> = ({ items, activeIndex, onSelect, busy }) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Measure active item's bounding rect and compute the indicator's
  // top/left relative to the menu container. `useLayoutEffect` runs
  // before paint so the indicator never visibly snaps from (0,0).
  useLayoutEffect(() => {
    const menu = menuRef.current;
    const item = itemRefs.current[activeIndex] ?? null;
    if (!menu || !item) {
      setPos(null);
      return;
    }
    const menuRect = menu.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();

    // Vertical center inside the menu's coordinate system, offset by
    // half the indicator height (4px → 2px) so the bar is vertically
    // centred on the label.
    const top = itemRect.top - menuRect.top + itemRect.height / 2 - 2;

    // Horizontal position. The indicator ALWAYS sits to the LEFT of the
    // active item's label — in both LTR and RTL. Items inside the menu
    // are right-aligned in both locales (via `align-items: flex-end` /
    // RTL-direction-aware `flex-start`), so the bar visually trails to
    // the inline-end (LTR right of curtain) or to the curtain-centre
    // side (RTL: menu sits on the left of curtain, indicator extends
    // further left of the label).
    //
    // In RTL the indicator may extend past `menuRect.left` — menu has
    // `overflow: visible` so it shows correctly.
    const left = itemRect.left - menuRect.left - INDICATOR_GAP_PX - INDICATOR_WIDTH_PX;

    setPos({ top, left });
  }, [activeIndex, items]);

  return (
    <div className={styles.menu} ref={menuRef}>
      {items.map((item, i) => {
        const isActive = i === activeIndex;
        return (
          <button
            key={item.key}
            ref={(el) => {
              itemRefs.current[i] = el;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={cx(styles.item, isActive && styles.itemActive)}
            onClick={() => {
              if (busy) return;
              onSelect(i);
            }}
            onPointerDown={(e) => e.preventDefault()}
          >
            <span className={styles.label}>{item.label}</span>
          </button>
        );
      })}
      {pos && (
        <span
          className={styles.indicator}
          style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

DesktopSideMenu.displayName = 'DesktopSideMenu';
